import { USA_PROVINCE_NAMES } from "@redotech/locale/provinces";
import { Carriers } from "../../fulfillments/fulfillment-carriers-and-services";
import { RedoShopifyDeliveryMethodType } from "../../fulfillments/fulfillment-delivery-method-type";
import {
  FulfilledBy,
  FulfillmentGroupPickStatus,
} from "../../fulfillments/fulfillment-group";
import { ShipmentTrackerStatus } from "../../fulfillments/outbound-shipment-label";
import { ShopifyRiskAssessmentResult } from "../../fulfillments/shopify-fulfillment-schemas";
import { Provider } from "../../order";
import { ArrayStringFilterOperator } from "../advanced-filters/array-string-filter";
import { BooleanFilterOperator } from "../advanced-filters/boolean-filter";
import { DateFilterOperator } from "../advanced-filters/date-filter";
import { ExistsFilterOperator } from "../advanced-filters/exists-filter";
import { AdvancedFilterType } from "../advanced-filters/generic-advanced-filter-data";
import { NumberFilterOperator } from "../advanced-filters/number-filter";
import {
  VALUE_FORMAT_ARRAY_TO_STRING,
  VALUE_FORMAT_BOOLEAN,
  VALUE_FORMAT_DATE,
  VALUE_FORMAT_EXISTS,
  VALUE_FORMAT_NUMBER,
} from "./agent-filter";
import { FilterCatalogEntry } from "./filter-catalog-entry";

export interface FGFilterCatalogOptions {
  wmsEnabled?: boolean;
  customFields?: FGCustomFieldConfig[];
}

/** The known custom string field keys that support dynamic value lookups. */
export type FGCustomStringFieldKey =
  | "customString1"
  | "customString2"
  | "customString3"
  | "customAttributeString1"
  | "customAttributeString2"
  | "customAttributeString3"
  | "variantCustomString1"
  | "variantCustomString2"
  | "variantCustomString3";

export type FGCustomFieldConfig =
  | { fieldKey: string; displayName: string; type: "date" | "number" }
  | { fieldKey: FGCustomStringFieldKey; displayName: string; type: "string" };

// ── Shared operator groups ───────────────────────────────────────────────────

const DATE_OPS = [
  DateFilterOperator.WITHIN,
  DateFilterOperator.BEFORE,
  DateFilterOperator.AFTER,
] as const;

const NUMBER_OPS = [
  NumberFilterOperator.EQUAL,
  NumberFilterOperator.LESS_THAN,
  NumberFilterOperator.GREATER_THAN,
  NumberFilterOperator.BETWEEN,
] as const;

const BOOL_OPS = [
  BooleanFilterOperator.IS,
  BooleanFilterOperator.IS_NOT,
] as const;

const EXISTS_OPS = [
  ExistsFilterOperator.EXISTS,
  ExistsFilterOperator.NOT_EXISTS,
] as const;

// ── Individual filter entries ────────────────────────────────────────────────
// Each key matches the AdvancedFilterData.name field used in the frontend.
// The frontend imports these entries and extends them with UI-specific fields.

export const fgFilterEntries = {
  // ── DATE ──────────────────────────────────────────────────────────────────

  createdAt: {
    name: "createdAt",
    displayName: "Shipment created",
    agentDescription:
      "When the fulfillment group record was created in Redo OMS. Use this to filter by when the shipment was first created in the system, not when the customer placed the order.",
    type: AdvancedFilterType.DATE,
    operators: DATE_OPS,
    valueFormat: VALUE_FORMAT_DATE,
  },

  orderCreatedAt: {
    name: "orderCreatedAt",
    displayName: "Order created",
    agentDescription:
      "When the customer originally placed the order on the sales channel (e.g., Shopify). Different from 'Shipment created' — use this when filtering by order date.",
    type: AdvancedFilterType.DATE,
    operators: DATE_OPS,
    valueFormat: VALUE_FORMAT_DATE,
  },

  shippedDate: {
    name: "shippedDate",
    displayName: "Fulfilled on",
    agentDescription:
      "When the shipping label was purchased and the order was marked as shipped. Null for orders that have not yet shipped.",
    type: AdvancedFilterType.DATE,
    operators: DATE_OPS,
    valueFormat: VALUE_FORMAT_DATE,
  },

  labelPurchasedDate: {
    name: "labelPurchasedDate",
    displayName: "Label purchased on",
    agentDescription:
      "When the shipping label was purchased for this fulfillment group.",
    type: AdvancedFilterType.DATE,
    operators: DATE_OPS,
    valueFormat: VALUE_FORMAT_DATE,
  },

  estimatedDeliveryDate: {
    name: "estimatedDeliveryDate",
    displayName: "Estimated delivery date",
    agentDescription: "The carrier's estimated delivery date for the shipment.",
    type: AdvancedFilterType.DATE,
    operators: DATE_OPS,
    valueFormat: VALUE_FORMAT_DATE,
  },

  packingSlipPrintedAt: {
    name: "packingSlipPrintedAt",
    displayName: "Packing slip printed at",
    agentDescription: "When the packing slip was last printed for this order.",
    type: AdvancedFilterType.DATE,
    operators: DATE_OPS,
    valueFormat: VALUE_FORMAT_DATE,
  },

  slaDeliveryPromise: {
    name: "slaDeliveryPromise",
    displayName: "Fulfillment promise",
    agentDescription:
      "The SLA delivery promise date — the deadline by which the order should be fulfilled.",
    type: AdvancedFilterType.DATE,
    operators: DATE_OPS,
    valueFormat: VALUE_FORMAT_DATE,
  },

  // ── ARRAY_TO_STRING (static values) ──────────────────────────────────────

  source: {
    name: "source",
    displayName: "Source",
    agentDescription:
      "The sales channel or platform the order originated from. Values are Provider enum strings (e.g., 'shopify', 'commentsold', 'bigcommerce').",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    operators: [
      ArrayStringFilterOperator.ANY_OF,
      ArrayStringFilterOperator.ALL_OF,
      ArrayStringFilterOperator.NONE_OF,
    ],
    staticValues: [
      Provider.SHOPIFY,
      Provider.COMMENTSOLD,
      Provider.BIGCOMMERCE,
      Provider.NETSUITE,
      Provider.TIKTOK,
      Provider.AMAZON,
      Provider.WALMART,
      Provider.EBAY,
      Provider.FAIRE,
      Provider.WOOCOMMERCE,
      Provider.APPAREL_MAGIC,
      Provider.AIMS360,
      Provider.CIN7_OMNI,
      Provider.CIN7_CORE,
      Provider.MANUAL,
      Provider.REDO_MARKETPLACE,
      Provider.WAYFAIR,
      Provider.BRIGHTPEARL,
      Provider.LINNWORKS,
    ],
    valueFormat: VALUE_FORMAT_ARRAY_TO_STRING,
  },

  deliveryMethod: {
    name: "deliveryMethod",
    displayName: "Delivery method",
    agentDescription:
      "How the order will be fulfilled. SHIPPING for standard shipments sent to the customer. PICK_UP for in-store orders.",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    operators: [
      ArrayStringFilterOperator.ANY_OF,
      ArrayStringFilterOperator.ALL_OF,
      ArrayStringFilterOperator.NONE_OF,
    ],
    staticValues: Object.values(RedoShopifyDeliveryMethodType),
    valueFormat: VALUE_FORMAT_ARRAY_TO_STRING,
  },

  selectedCarrier: {
    name: "selectedCarrier",
    displayName: "Selected carrier",
    agentDescription:
      "The carrier selected for the shipment (e.g., USPS, UPS, FedEx, DHL). Based on what was actually chosen, not what was requested at checkout.",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    operators: [
      ArrayStringFilterOperator.ANY_OF,
      ArrayStringFilterOperator.ALL_OF,
      ArrayStringFilterOperator.NONE_OF,
    ],
    staticValues: Object.values(Carriers).filter(
      (c) =>
        c !== Carriers.UPS_SUREPOST &&
        c !== Carriers.USPS_RETURNS &&
        c !== Carriers.USPS_SHIP &&
        c !== Carriers.FEDEX_SMART_POST &&
        c !== Carriers.UPS_MAIL_INNOVATIONS,
    ),
    valueFormat: VALUE_FORMAT_ARRAY_TO_STRING,
  },

  deliveryStatus: {
    name: "deliveryStatus",
    displayName: "Delivery status",
    agentDescription:
      "The in-transit delivery status reported by the carrier (e.g., 'In Transit', 'Delivered', 'Out for Delivery', 'Exception').",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    operators: [
      ArrayStringFilterOperator.ANY_OF,
      ArrayStringFilterOperator.NONE_OF,
    ],
    staticValues: Object.values(ShipmentTrackerStatus),
    valueFormat: VALUE_FORMAT_ARRAY_TO_STRING,
  },

  state: {
    name: "state",
    displayName: "State",
    agentDescription:
      "The destination US state or province. Values are state/province names (e.g., 'California', 'New York', 'Texas').",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    operators: [
      ArrayStringFilterOperator.ANY_OF,
      ArrayStringFilterOperator.ALL_OF,
      ArrayStringFilterOperator.NONE_OF,
    ],
    staticValues: USA_PROVINCE_NAMES,
    valueFormat: VALUE_FORMAT_ARRAY_TO_STRING,
  },

  fulfilledBy: {
    name: "fulfilledBy",
    displayName: "Fulfilled by",
    agentDescription:
      "If the order has been fulfilled, was it fulfilled by Redo's OMS, or was it fulfilled outside of Redo",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    operators: [
      ArrayStringFilterOperator.ANY_OF,
      ArrayStringFilterOperator.NONE_OF,
    ],
    staticValues: Object.values(FulfilledBy),
    valueFormat: VALUE_FORMAT_ARRAY_TO_STRING,
  },

  marketplaceNotificationStatus: {
    name: "marketplaceNotificationStatus",
    displayName: "Marketplace notification",
    agentDescription:
      "The marketplace notification status for the fulfillment group. Values: 'complete', 'skipped', 'pending', 'failed'.",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    operators: [
      ArrayStringFilterOperator.ANY_OF,
      ArrayStringFilterOperator.NONE_OF,
    ],
    staticValues: ["complete", "skipped", "pending", "failed"],
    valueFormat: VALUE_FORMAT_ARRAY_TO_STRING,
  },

  fraudRisk: {
    name: "fraudRisk",
    displayName: "Fraud risk",
    agentDescription:
      "Fraud risk. Only relevent to channels that provide fraud risk assessment.",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    operators: [
      ArrayStringFilterOperator.ANY_OF,
      ArrayStringFilterOperator.ALL_OF,
      ArrayStringFilterOperator.NONE_OF,
    ],
    staticValues: Object.values(ShopifyRiskAssessmentResult),
    valueFormat: VALUE_FORMAT_ARRAY_TO_STRING,
  },

  orderNote: {
    name: "orderNote",
    displayName: "Order note",
    agentDescription:
      "Notes attached to the order. Can check for specific text (CONTAINS) or whether any note exists (IS_NOT_EMPTY).",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    operators: [
      ArrayStringFilterOperator.IS_EMPTY,
      ArrayStringFilterOperator.IS_NOT_EMPTY,
      ArrayStringFilterOperator.CONTAINS,
      ArrayStringFilterOperator.NOT_CONTAINS,
    ],
    valueFormat: VALUE_FORMAT_ARRAY_TO_STRING,
  },

  // ── ARRAY_TO_STRING (dynamic values) ─────────────────────────────────────

  status: {
    name: "status",
    displayName: "Status",
    agentDescription:
      "The current fulfillment status of the order. Call get-filter-values with 'fg:status' to see all available statuses for this team. Never create a view with Scheduled, Pending, ThirdPartyInProgress, or Incomplete statuses. Nobody uses those",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    operators: [
      ArrayStringFilterOperator.ANY_OF,
      ArrayStringFilterOperator.NONE_OF,
    ],
    dynamicValuesKey: "fg:status",
    valueFormat: VALUE_FORMAT_ARRAY_TO_STRING,
  },

  requestedService: {
    name: "requestedService",
    displayName: "Requested service",
    agentDescription:
      "The shipping service the customer selected at checkout (e.g., 'Standard Shipping', 'Express Shipping'). Matches checkout option names configured in Redo.",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    operators: [
      ArrayStringFilterOperator.NONE_OF,
      ArrayStringFilterOperator.ALL_OF,
      ArrayStringFilterOperator.ANY_OF,
    ],
    dynamicValuesKey: "fg:requestedService",
    valueFormat: VALUE_FORMAT_ARRAY_TO_STRING,
  },

  stores: {
    name: "stores",
    displayName: "Sales channel",
    agentDescription:
      "The specific Shopify store or sales channel the order came from. Useful for merchants with multiple storefronts.",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    operators: [
      ArrayStringFilterOperator.ANY_OF,
      ArrayStringFilterOperator.ALL_OF,
      ArrayStringFilterOperator.NONE_OF,
    ],
    dynamicValuesKey: "fg:stores",
    valueFormat: VALUE_FORMAT_ARRAY_TO_STRING,
  },

  originStore: {
    name: "originStore",
    displayName: "Origin store",
    agentDescription:
      "The store/brand that the order belong to. Useful for 3PLs that fulfill for multiple brands.",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    operators: [
      ArrayStringFilterOperator.ANY_OF,
      ArrayStringFilterOperator.ALL_OF,
      ArrayStringFilterOperator.NONE_OF,
    ],
    dynamicValuesKey: "fg:originStore",
    valueFormat: VALUE_FORMAT_ARRAY_TO_STRING,
  },

  countries: {
    name: "countries",
    displayName: "Country",
    agentDescription:
      "The destination country for the shipment. Use country codes (e.g., 'US', 'CA', 'GB'). Use NONE_OF 'US' to filter for international orders.",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    operators: [
      ArrayStringFilterOperator.NONE_OF,
      ArrayStringFilterOperator.ALL_OF,
      ArrayStringFilterOperator.ANY_OF,
    ],
    dynamicValuesKey: "fg:countries",
    valueFormat: VALUE_FORMAT_ARRAY_TO_STRING,
  },

  itemSkus: {
    name: "itemSkus",
    displayName: "Item SKU",
    agentDescription:
      "Filter by product SKU codes present in the order. Use ANY_OF to match orders containing any of the specified SKUs.",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    operators: [
      ArrayStringFilterOperator.ANY_OF,
      ArrayStringFilterOperator.ALL_OF,
      ArrayStringFilterOperator.NONE_OF,
      ArrayStringFilterOperator.CONTAINS,
    ],
    dynamicValuesKey: "fg:itemSkus",
    valueFormat: VALUE_FORMAT_ARRAY_TO_STRING,
  },

  itemTitle: {
    name: "itemTitle",
    displayName: "Item title",
    agentDescription:
      "Filter by product name or title in the order. Supports partial text matching with CONTAINS.",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    operators: [
      ArrayStringFilterOperator.NONE_OF,
      ArrayStringFilterOperator.ALL_OF,
      ArrayStringFilterOperator.ANY_OF,
      ArrayStringFilterOperator.CONTAINS,
      ArrayStringFilterOperator.NOT_CONTAINS,
    ],
    dynamicValuesKey: "fg:itemTitle",
    valueFormat: VALUE_FORMAT_ARRAY_TO_STRING,
  },

  productVariant: {
    name: "productVariant",
    displayName: "Product variant",
    agentDescription:
      "Filter by product variant name (e.g., 'Blue / Large', 'Red / Small').",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    operators: [ArrayStringFilterOperator.ANY_OF],
    dynamicValuesKey: "fg:productVariant",
    valueFormat: VALUE_FORMAT_ARRAY_TO_STRING,
  },

  vendors: {
    name: "vendors",
    displayName: "Vendor",
    agentDescription: "The product vendor or brand for items in the order.",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    operators: [
      ArrayStringFilterOperator.ANY_OF,
      ArrayStringFilterOperator.ALL_OF,
      ArrayStringFilterOperator.NONE_OF,
    ],
    dynamicValuesKey: "fg:vendors",
    valueFormat: VALUE_FORMAT_ARRAY_TO_STRING,
  },

  fulfillmentLocation: {
    name: "fulfillmentLocation",
    displayName: "Fulfillment location",
    agentDescription:
      "The name of the ship-from address or warehouse location.",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    operators: [
      ArrayStringFilterOperator.ANY_OF,
      ArrayStringFilterOperator.NONE_OF,
    ],
    dynamicValuesKey: "fg:originAddressName",
    valueFormat: VALUE_FORMAT_ARRAY_TO_STRING,
  },

  customer: {
    name: "customer",
    displayName: "Customer name",
    agentDescription:
      "The customer's name on the order. Supports partial matching with CONTAINS.",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    operators: [
      ArrayStringFilterOperator.NONE_OF,
      ArrayStringFilterOperator.ALL_OF,
      ArrayStringFilterOperator.ANY_OF,
    ],
    dynamicValuesKey: "fg:customer",
    valueFormat: VALUE_FORMAT_ARRAY_TO_STRING,
  },

  tagName: {
    name: "tagName",
    displayName: "Tag",
    agentDescription:
      "Internal Redo tags when the merchant specifies the use of tags, if they don't call out using order tags, sales channel tags, product tags, or marketplace tags - this is what we should use",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    operators: [
      ArrayStringFilterOperator.NONE_OF,
      ArrayStringFilterOperator.ALL_OF,
      ArrayStringFilterOperator.ANY_OF,
    ],
    dynamicValuesKey: "fg:tagName",
    valueFormat: VALUE_FORMAT_ARRAY_TO_STRING,
  },

  shopifyOrderTags: {
    name: "shopifyOrderTags",
    displayName: "Marketplace order tag",
    agentDescription:
      "Tags on the order coming from the sales channel. Not actually just shopify tags",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    operators: [
      ArrayStringFilterOperator.ANY_OF,
      ArrayStringFilterOperator.ALL_OF,
      ArrayStringFilterOperator.NONE_OF,
      ArrayStringFilterOperator.CONTAINS,
    ],
    dynamicValuesKey: "fg:shopifyOrderTags",
    valueFormat: VALUE_FORMAT_ARRAY_TO_STRING,
  },

  productTags: {
    name: "productTags",
    displayName: "Product tags",
    agentDescription: "Tags on products within the order.",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    operators: [
      ArrayStringFilterOperator.ANY_OF,
      ArrayStringFilterOperator.ALL_OF,
      ArrayStringFilterOperator.NONE_OF,
    ],
    dynamicValuesKey: "fg:productTags",
    valueFormat: VALUE_FORMAT_ARRAY_TO_STRING,
  },

  order: {
    name: "order",
    displayName: "Order",
    agentDescription:
      "The order number or name (e.g., '#1234'). Use CONTAINS to search for partial order numbers.",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    operators: [
      ArrayStringFilterOperator.ANY_OF,
      ArrayStringFilterOperator.NONE_OF,
      ArrayStringFilterOperator.ALL_OF,
      ArrayStringFilterOperator.CONTAINS,
      ArrayStringFilterOperator.NOT_CONTAINS,
    ],
    dynamicValuesKey: "fg:order",
    valueFormat: VALUE_FORMAT_ARRAY_TO_STRING,
  },

  bin: {
    name: "bin",
    displayName: "Bin",
    agentDescription:
      "The warehouse bin or shelf location assigned to items in this order.",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    operators: [
      ArrayStringFilterOperator.ANY_OF,
      ArrayStringFilterOperator.ALL_OF,
      ArrayStringFilterOperator.NONE_OF,
      ArrayStringFilterOperator.STARTS_WITH,
      ArrayStringFilterOperator.DOES_NOT_START_WITH,
    ],
    dynamicValuesKey: "fg:bin",
    valueFormat: VALUE_FORMAT_ARRAY_TO_STRING,
  },

  batchId: {
    name: "batchId",
    displayName: "Batch ID",
    agentDescription: "The ID of the batch that labels were purchased for.",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    operators: [
      ArrayStringFilterOperator.NONE_OF,
      ArrayStringFilterOperator.ALL_OF,
      ArrayStringFilterOperator.ANY_OF,
    ],
    dynamicValuesKey: "fg:batchId",
    valueFormat: VALUE_FORMAT_ARRAY_TO_STRING,
  },

  voidedBatchId: {
    name: "voidedBatchId",
    displayName: "Voided batch ID",
    agentDescription:
      "The ID of a batch that was voided. Useful for finding orders whose batch was cancelled.",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    operators: [
      ArrayStringFilterOperator.NONE_OF,
      ArrayStringFilterOperator.ALL_OF,
      ArrayStringFilterOperator.ANY_OF,
    ],
    dynamicValuesKey: "fg:voidedBatchId",
    valueFormat: VALUE_FORMAT_ARRAY_TO_STRING,
  },

  selectedParcelsName: {
    name: "selectedParcelsName",
    displayName: "Package name",
    agentDescription:
      "The parcel or box type selected for this shipment (e.g., 'Small Box', 'Poly Mailer').",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    operators: [
      ArrayStringFilterOperator.NONE_OF,
      ArrayStringFilterOperator.ALL_OF,
      ArrayStringFilterOperator.ANY_OF,
    ],
    dynamicValuesKey: "fg:selectedParcelsName",
    valueFormat: VALUE_FORMAT_ARRAY_TO_STRING,
  },

  assignees: {
    name: "assignees",
    displayName: "Assignee",
    agentDescription:
      "The warehouse worker assigned to process or handle this order.",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    operators: [
      ArrayStringFilterOperator.ANY_OF,
      ArrayStringFilterOperator.NONE_OF,
    ],
    dynamicValuesKey: "fg:assignees",
    valueFormat: VALUE_FORMAT_ARRAY_TO_STRING,
  },

  pickSessionIdValue: {
    name: "pickSessionIdValue",
    displayName: "Pick session (ID)",
    agentDescription: "The pick session ID this order belongs to.",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    operators: [
      ArrayStringFilterOperator.ANY_OF,
      ArrayStringFilterOperator.NONE_OF,
    ],
    valueFormat: VALUE_FORMAT_ARRAY_TO_STRING,
  },

  assignedFulfillmentService: {
    name: "assignedFulfillmentService",
    displayName: "Fulfillment service",
    agentDescription:
      "The fulfillment service (3PL) assigned to handle this order.",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    operators: [
      ArrayStringFilterOperator.ANY_OF,
      ArrayStringFilterOperator.NONE_OF,
    ],
    dynamicValuesKey: "fg:assignedFulfillmentService",
    valueFormat: VALUE_FORMAT_ARRAY_TO_STRING,
  },

  // ── NUMBER ────────────────────────────────────────────────────────────────

  itemQuantity: {
    name: "itemQuantity",
    displayName: "Item quantity",
    agentDescription:
      "Total quantity of units across all line items in the order.",
    type: AdvancedFilterType.NUMBER,
    operators: NUMBER_OPS,
    valueFormat: VALUE_FORMAT_NUMBER,
  },

  expandedItemQuantity: {
    name: "expandedItemQuantity",
    displayName: "Expanded item quantity",
    agentDescription:
      "Total unit count after expanding bundled or kit items into their individual components.",
    type: AdvancedFilterType.NUMBER,
    operators: NUMBER_OPS,
    valueFormat: VALUE_FORMAT_NUMBER,
  },

  totalPrice: {
    name: "totalPrice",
    displayName: "Total price",
    agentDescription:
      "The total order value in the merchant's store currency. Not the discounted price.",
    type: AdvancedFilterType.NUMBER,
    operators: NUMBER_OPS,
    valueFormat: VALUE_FORMAT_NUMBER,
  },

  parcelWeight: {
    name: "parcelWeight",
    displayName: "Total weight",
    agentDescription:
      "The total shipment weight in grams. Convert from pounds/ounces as needed (1 lb = 453.6g, 1 oz = 28.35g). Example: 10 lbs = 4536.",
    type: AdvancedFilterType.NUMBER,
    operators: NUMBER_OPS,
    valueFormat: VALUE_FORMAT_NUMBER,
  },

  lineItemQuantity: {
    name: "lineItemQuantity",
    displayName: "Unique product count",
    agentDescription:
      "The number of distinct product lines in the order (not total units).",
    type: AdvancedFilterType.NUMBER,
    operators: NUMBER_OPS,
    valueFormat: VALUE_FORMAT_NUMBER,
  },

  customerOrderCount: {
    name: "customerOrderCount",
    displayName: "Customer order count",
    agentDescription:
      "How many total orders this customer has placed with the merchant.",
    type: AdvancedFilterType.NUMBER,
    operators: NUMBER_OPS,
    valueFormat: VALUE_FORMAT_NUMBER,
  },

  fulfillmentOrderCount: {
    name: "fulfillmentOrderCount",
    displayName: "Order count",
    agentDescription: "Number of orders merged into this fulfillment group.",
    type: AdvancedFilterType.NUMBER,
    operators: NUMBER_OPS,
    valueFormat: VALUE_FORMAT_NUMBER,
  },

  // ── BOOLEAN ───────────────────────────────────────────────────────────────

  labelPrinted: {
    name: "labelPrinted",
    displayName: "Label printed",
    agentDescription: "Whether a shipping label has been printed.",
    type: AdvancedFilterType.BOOLEAN,
    operators: [BooleanFilterOperator.IS],
    valueFormat: VALUE_FORMAT_BOOLEAN,
  },

  pickListPrinted: {
    name: "pickListPrinted",
    displayName: "Pick list printed",
    agentDescription: "Whether a pick list has been printed for this order.",
    type: AdvancedFilterType.BOOLEAN,
    operators: [BooleanFilterOperator.IS],
    valueFormat: VALUE_FORMAT_BOOLEAN,
  },

  packingSlipPrinted: {
    name: "packingSlipPrinted",
    displayName: "Packing slip printed",
    agentDescription: "Whether a packing slip has been printed.",
    type: AdvancedFilterType.BOOLEAN,
    operators: [BooleanFilterOperator.IS],
    valueFormat: VALUE_FORMAT_BOOLEAN,
  },

  usedWeightLearning: {
    name: "usedWeightLearning",
    displayName: "Used weight learning",
    agentDescription:
      "Whether Redo's AI weight learning was used to determine the shipment weight.",
    type: AdvancedFilterType.BOOLEAN,
    operators: BOOL_OPS,
    valueFormat: VALUE_FORMAT_BOOLEAN,
  },

  usedParcelLearning: {
    name: "usedParcelLearning",
    displayName: "Used package learning",
    agentDescription:
      "Whether Redo's AI parcel selection learning was used to choose the box/parcel.",
    type: AdvancedFilterType.BOOLEAN,
    operators: BOOL_OPS,
    valueFormat: VALUE_FORMAT_BOOLEAN,
  },

  commercialInvoiceElectronic: {
    name: "commercialInvoiceElectronic",
    displayName: "Comm. invoice e-submitted",
    agentDescription:
      "Whether the commercial invoice for international shipping is submitted electronically to the carrier.",
    type: AdvancedFilterType.BOOLEAN,
    operators: [BooleanFilterOperator.IS],
    valueFormat: VALUE_FORMAT_BOOLEAN,
  },

  addressVerified: {
    name: "addressVerified",
    displayName: "Address verified",
    agentDescription:
      "Whether the shipping address has been verified as deliverable.",
    type: AdvancedFilterType.BOOLEAN,
    operators: [BooleanFilterOperator.IS],
    valueFormat: VALUE_FORMAT_BOOLEAN,
  },

  missingBins: {
    name: "missingBins",
    displayName: "Missing bins",
    agentDescription:
      "Whether the order contains items that have no bin/shelf location assigned in the warehouse.",
    type: AdvancedFilterType.BOOLEAN,
    operators: [BooleanFilterOperator.IS],
    valueFormat: VALUE_FORMAT_BOOLEAN,
  },

  // ── EXISTS ────────────────────────────────────────────────────────────────

  scanVerified: {
    name: "scanVerified",
    displayName: "Scan verified",
    agentDescription:
      "Whether all items in the order have been scan-verified during pick/pack.",
    type: AdvancedFilterType.EXISTS,
    operators: EXISTS_OPS,
    valueFormat: VALUE_FORMAT_EXISTS,
  },

  pickSessionId: {
    name: "pickSessionId",
    displayName: "Pick session",
    agentDescription:
      "Whether this order is currently part of an active pick session.",
    type: AdvancedFilterType.EXISTS,
    operators: EXISTS_OPS,
    valueFormat: VALUE_FORMAT_EXISTS,
  },
} satisfies Record<string, FilterCatalogEntry>;

export type FGFilterKey = keyof typeof fgFilterEntries;

// ── WMS-only entries ─────────────────────────────────────────────────────────

export const fgWmsFilterEntries = {
  pickedAt: {
    name: "pickedAt",
    displayName: "Picked on",
    agentDescription: "When items were picked in the warehouse for this order.",
    type: AdvancedFilterType.DATE,
    operators: DATE_OPS,
    valueFormat: VALUE_FORMAT_DATE,
  },

  pickStartedOn: {
    name: "pickStartedOn",
    displayName: "Pick started on",
    agentDescription:
      "When the pick session for this order was started in the warehouse.",
    type: AdvancedFilterType.DATE,
    operators: DATE_OPS,
    valueFormat: VALUE_FORMAT_DATE,
  },

  pickStatus: {
    name: "pickStatus",
    displayName: "Pick status",
    agentDescription:
      "The warehouse pick status for this order in the WMS (e.g., 'Pending', 'In Progress', 'Picked').",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    operators: [
      ArrayStringFilterOperator.ANY_OF,
      ArrayStringFilterOperator.NONE_OF,
    ],
    valueFormat: VALUE_FORMAT_ARRAY_TO_STRING,
    staticValues: Object.values(FulfillmentGroupPickStatus),
  },

  assignedPicker: {
    name: "assignedPicker",
    displayName: "Assigned picker",
    agentDescription:
      "The warehouse team member assigned to pick items for this order.",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    operators: [
      ArrayStringFilterOperator.ANY_OF,
      ArrayStringFilterOperator.NONE_OF,
    ],
    dynamicValuesKey: "fg:assignedPicker",
    valueFormat: VALUE_FORMAT_ARRAY_TO_STRING,
  },

  pickability: {
    name: "pickability",
    displayName: "Pickability",
    agentDescription:
      "Whether all items in the order have sufficient inventory in pickable bins. 'all_pickable' means every item has enough stock, 'partially_pickable' means some items do, 'not_pickable' means none do.",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    operators: [
      ArrayStringFilterOperator.ANY_OF,
      ArrayStringFilterOperator.NONE_OF,
    ],
    staticValues: [
      "all_pickable",
      "partially_pickable",
      "not_pickable",
      "all_pending_wo",
      "partially_pending_wo",
    ],
    valueFormat: VALUE_FORMAT_ARRAY_TO_STRING,
  },

  missingPickItemSkus: {
    name: "missingPickItemSkus",
    displayName: "Missing item SKU",
    agentDescription:
      "Filter by SKUs of items that could not be picked due to insufficient inventory.",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    operators: [
      ArrayStringFilterOperator.ANY_OF,
      ArrayStringFilterOperator.NONE_OF,
      ArrayStringFilterOperator.CONTAINS,
    ],
    dynamicValuesKey: "fg:missingPickItemSkus",
    valueFormat: VALUE_FORMAT_ARRAY_TO_STRING,
  },

  warehouseMissingBins: {
    name: "warehouseMissingBins",
    displayName: "Warehouse missing bins",
    agentDescription:
      "Whether any item in the order has no assigned warehouse storage location (bin or storage type). Receiving-only locations do not count as assigned.",
    type: AdvancedFilterType.BOOLEAN,
    operators: [BooleanFilterOperator.IS],
    valueFormat: VALUE_FORMAT_BOOLEAN,
  },

  productBinAssignmentDate: {
    name: "productBinAssignmentDate",
    displayName: "Product bin assignment date",
    agentDescription:
      "Filters on the order's product bin assignment date — the latest, across line items, of each item's earliest assignment to a bin/storage location (receiving excluded). Orders where any line item has no bin/storage assignment never match.",
    type: AdvancedFilterType.DATE,
    operators: DATE_OPS,
    valueFormat: VALUE_FORMAT_DATE,
  },

  pickableBinCount: {
    name: "pickableBinCount",
    displayName: "Pickable bins per item",
    agentDescription:
      "The number of distinct pickable bins (type 'bin') each item in the order is assigned to. Matches FGs where any line item's count satisfies the condition.",
    type: AdvancedFilterType.NUMBER,
    operators: [
      NumberFilterOperator.EQUAL,
      NumberFilterOperator.LESS_THAN,
      NumberFilterOperator.GREATER_THAN,
      NumberFilterOperator.BETWEEN,
    ],
    valueFormat: VALUE_FORMAT_NUMBER,
  },
} satisfies Record<string, FilterCatalogEntry>;

export type FGWmsFilterKey = keyof typeof fgWmsFilterEntries;

// ── Custom field entry generator ─────────────────────────────────────────────

export function getCustomFieldCatalogEntry(
  field: FGCustomFieldConfig,
): FilterCatalogEntry {
  switch (field.type) {
    case "date":
      return {
        name: field.fieldKey,
        displayName: field.displayName,
        agentDescription: `Custom date field: ${field.displayName}`,
        type: AdvancedFilterType.DATE,
        operators: DATE_OPS,
        valueFormat: VALUE_FORMAT_DATE,
      };
    case "number":
      return {
        name: field.fieldKey,
        displayName: field.displayName,
        agentDescription: `Custom number field: ${field.displayName}`,
        type: AdvancedFilterType.NUMBER,
        operators: NUMBER_OPS,
        valueFormat: VALUE_FORMAT_NUMBER,
      };
    case "string":
      return {
        name: field.fieldKey,
        displayName: field.displayName,
        agentDescription: `Custom text field: ${field.displayName}`,
        type: AdvancedFilterType.ARRAY_TO_STRING,
        operators: [
          ArrayStringFilterOperator.NONE_OF,
          ArrayStringFilterOperator.ANY_OF,
          ArrayStringFilterOperator.ALL_OF,
        ],
        dynamicValuesKey: `fg:${field.fieldKey}`,
        valueFormat: VALUE_FORMAT_ARRAY_TO_STRING,
      };
  }
}

// ── Derived type unions ──────────────────────────────────────────────────────

/** Union of all base-catalog filter name literals */
export type FGFilterName = (typeof fgFilterEntries)[FGFilterKey]["name"];

/** Union of all WMS-catalog filter name literals */
export type FGWmsFilterName =
  (typeof fgWmsFilterEntries)[FGWmsFilterKey]["name"];

// ── Public API for agent tools and bulk access ───────────────────────────────

export function getFGFilterCatalog(
  options: FGFilterCatalogOptions = {},
): FilterCatalogEntry[] {
  const filters: FilterCatalogEntry[] = Object.values(fgFilterEntries);

  if (options.wmsEnabled) {
    filters.push(...Object.values(fgWmsFilterEntries));
  }

  for (const field of options.customFields ?? []) {
    filters.push(getCustomFieldCatalogEntry(field));
  }

  return filters.sort((a, b) => a.displayName.localeCompare(b.displayName));
}
